import { PlusOutlined } from "@ant-design/icons";
import {
  Badge,
  Button,
  Card,
  Divider,
  Drawer,
  Flex,
  Input,
  Popconfirm,
  Radio,
  Select,
  Space,
  Typography,
} from "antd";
import Title from "antd/es/typography/Title";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import api from "../../../../api";
import Loader from "../../../../components/Loader";
import { IntTplClientDocumentType } from "../../../../domain/types";
import { useAppSelector } from "../../../../store";
import { AppState } from "../../../../store/appSlice";
import AdminLangSwicher from "../../../admin_components/AdminLangSwicher";
const Text = Typography;

const InternationalTplClientDocumentType = () => {
  const { t } = useTranslation();
  const { field_Language, lang } = useAppSelector(
    (data: { app: AppState }) => data.app,
  );
  const [drawerStatus, setDrawerStatus] = useState<boolean>(false);
  const [editedState, setEditedState] =
    useState<Partial<IntTplClientDocumentType> | null>(null);

  const { data: clientTypes, isLoading: isClientTypesLoading } =
    api.useGetClientTypesQuery(undefined);

  const {
    data: IntTplClientDodumentTypesList,
    refetch: refetchIntTplClientDocumentTypes,
    isLoading: isIntTplClientDocumentTypesLoading,
  } = api.useGetIntTplClientTDocumentypesQuery(undefined);

  const {
    data: intTplClientDocumentTypeById,
    isLoading: isIntTplClientDocumentTypeByIdLoading,
    isSuccess: intTplClientDocumentTypeSuccess,
    refetch: refetchClientDocumentTypeById,
  } = api.useGetIntTplClientDocumentTypeByIdQuery(editedState?.id as number, {
    skip: !editedState?.id,
  });

  const _refetClientDocumentType = useCallback(() => {
    if (editedState?.id) {
      refetchClientDocumentTypeById();
    }
  }, [editedState?.id, refetchClientDocumentTypeById]);

  useEffect(() => {
    _refetClientDocumentType();

    return () => {};
  }, [_refetClientDocumentType]);

  const [
    createClientDocumentType,
    { isLoading: isCreateIntTplClientDocumentTypeLoading },
  ] = api.useCreateIntTplClientDocumentTypeMutation();

  const [
    updateClientDocumentType,
    { isLoading: isUpdateIntTplClientDocumentTypeLoading },
  ] = api.useUpdateIntTplClientDocumentTypeMutation();

  const onCloseDrawer = () => {
    setDrawerStatus(false);
  };

  const setEditedField = (
    field: keyof IntTplClientDocumentType,
    value: any,
  ) => {
    setEditedState({
      ...editedState,
      [field]: value,
    });
  };

  const [
    deleteClientDocumentType,
    {
      isLoading: isDeleteIntTplClientDocumentTypeLoading,
      isSuccess: deleteIntTplClientDocumentTypeSuccess,
    },
  ] = api.useDeleteIntTplClientDocumentTypeMutation();

  const _createClientDocumentType = async () => {
    if (editedState) {
      try {
        await createClientDocumentType(editedState).unwrap();
        refetchIntTplClientDocumentTypes();
        toast.success(`${t("clientDocumentType")} ${t("addedSuccessfully")}`);
        onCloseDrawer();
      } catch (error) {
        if (error?.data?.errors?.constructor === Array) {
          const firstError = error.data.errors[0];
          const errorMessage = firstError.message;
          toast.error(errorMessage);
        } else {
          toast.error(`${t("errorOccurred")}`);
        }
      }
    } else {
      toast.error(`${t("fillAllFields")}`);
    }
  };

  const _updateClientDocumentType = async () => {
    if (editedState) {
      try {
        await updateClientDocumentType(editedState).unwrap();
        refetchIntTplClientDocumentTypes();
        toast.success(`${t("clientDocumentType")} ${t("updatedSuccessfully")}`);
        onCloseDrawer();
      } catch (error) {
        if (error?.data?.errors?.constructor === Array) {
          const firstError = error.data.errors[0];
          const errorMessage = firstError.message;
          toast.error(errorMessage);
        } else {
          toast.error(`${t("errorOccurred")}`);
        }
      }
    } else {
      toast.error(`${t("fillAllFields")}`);
    }
  };

  useEffect(() => {
    if (deleteIntTplClientDocumentTypeSuccess) {
      refetchIntTplClientDocumentTypes();
    }
  }, [deleteIntTplClientDocumentTypeSuccess, refetchIntTplClientDocumentTypes]);

  useEffect(() => {
    if (
      intTplClientDocumentTypeById &&
      intTplClientDocumentTypeSuccess &&
      intTplClientDocumentTypeById.id === editedState?.id
    ) {
      setEditedState(intTplClientDocumentTypeById);
      setDrawerStatus(true);
    }

    return () => {};
  }, [
    intTplClientDocumentTypeById,
    intTplClientDocumentTypeSuccess,
    editedState?.id,
  ]);

  const MainLoader = isIntTplClientDocumentTypesLoading || isClientTypesLoading;

  const DrawerLoader =
    isUpdateIntTplClientDocumentTypeLoading ||
    isDeleteIntTplClientDocumentTypeLoading ||
    isIntTplClientDocumentTypeByIdLoading ||
    isCreateIntTplClientDocumentTypeLoading;

  return (
    <>
      {MainLoader ? (
        <Loader />
      ) : (
        <>
          <Flex justify="space-between">
            <Button
              size="large"
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => [setEditedField("id", 0), setDrawerStatus(true)]}
            >
              {t("create")}
            </Button>

            <AdminLangSwicher />
          </Flex>

          <Divider />

          <Flex gap={20} wrap="wrap" justify="space-evenly">
            {IntTplClientDodumentTypesList?.map((item, index) => (
              <Badge.Ribbon
                key={index}
                style={{
                  marginTop: "-20px",
                }}
                text={item.status === "active" ? t("active") : t("inActive")}
                color={item.status === "active" ? "green" : "red"}
              >
                <Card
                  hoverable
                  style={{
                    maxWidth: "320px",
                    width: "100vw",
                    display: "grid",
                    placeItems: "center",
                    height: "160px",
                  }}
                  onClick={() => [
                    setEditedField("id", item.id),
                    setDrawerStatus(!drawerStatus),
                  ]}
                >
                  <Title level={5}>
                    {item.translations?.[field_Language]?.name ||
                      t("textNotFound")}
                  </Title>

                  <Text>
                    {t("docNumberOfPages")}: <b>{item?.neededSides}</b>
                  </Text>
                </Card>
              </Badge.Ribbon>
            ))}
          </Flex>

          <Drawer
            title={editedState?.id === 0 ? t("create") : t("update")}
            onClose={onCloseDrawer}
            open={drawerStatus}
            extra={<AdminLangSwicher />}
          >
            {DrawerLoader ? (
              <Loader />
            ) : (
              <Flex vertical gap={20}>
                <Space direction="vertical">
                  <Text>{t("clientDocumentType")}</Text>
                  <Input
                    size="large"
                    placeholder={t("clientDocumentType")}
                    value={
                      editedState?.translations?.[field_Language]?.name || ""
                    }
                    onChange={(event) =>
                      setEditedField("translations", {
                        ...editedState?.translations,
                        [field_Language]: {
                          ...editedState?.translations?.[field_Language],
                          name: event?.target?.value,
                        },
                      })
                    }
                  />
                </Space>

                <Radio.Group
                  onChange={(event) =>
                    setEditedField("clientTypeId", event?.target?.value)
                  }
                  value={editedState?.clientTypeId}
                  options={clientTypes?.map((item) => ({
                    label: item.translations?.[lang]?.name,
                    value: item.id,
                    className: item.slug,
                  }))}
                />

                <Space direction="vertical">
                  <Text>{t("docNumberOfPages")}</Text>
                  <Input
                    type="number"
                    size="large"
                    placeholder={t("docNumberOfPages")}
                    value={editedState?.neededSides || ""}
                    onChange={(event) =>
                      setEditedField("neededSides", event.target.value)
                    }
                  />
                </Space>

                <Space direction="vertical">
                  <Text>{t("status")}</Text>
                  <Select
                    size="large"
                    showSearch
                    style={{ width: "100%", display: "block" }}
                    placeholder={t("status")}
                    optionFilterProp="label"
                    allowClear
                    value={editedState?.status || undefined}
                    onChange={(value) => setEditedField("status", value)}
                    options={[
                      {
                        status: "active",
                        name: t("active"),
                      },
                      {
                        status: "inactive",
                        name: t("inActive"),
                      },
                    ]?.map((item) => ({
                      value: item.status,
                      label: item.name,
                    }))}
                  />
                </Space>

                <>
                  <Divider style={{ margin: 0 }} />
                  {editedState?.id === 0 ? (
                    <Button
                      disabled={
                        editedState && editedState.status ? false : true
                      }
                      size="large"
                      type="primary"
                      onClick={_createClientDocumentType}
                    >
                      {t("create")}
                    </Button>
                  ) : null}

                  {editedState && editedState.id && editedState.id !== 0 ? (
                    <>
                      <Button
                        size="large"
                        type="primary"
                        onClick={_updateClientDocumentType}
                      >
                        {t("update")}
                      </Button>

                      <Popconfirm
                        title={t("certainToDeleteIt")}
                        onConfirm={() => [
                          deleteClientDocumentType(editedState?.id as number),
                          onCloseDrawer(),
                        ]}
                        onCancel={() => {}}
                        okText="Yes"
                        cancelText="No"
                        placement="bottom"
                      >
                        <Button
                          danger
                          size="large"
                          style={{
                            width: "100%",
                          }}
                        >
                          {t("delete")}
                        </Button>
                      </Popconfirm>
                    </>
                  ) : null}
                </>
              </Flex>
            )}
          </Drawer>
        </>
      )}
    </>
  );
};

export default InternationalTplClientDocumentType;
